<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';

import { CAN_POST_MESSAGE } from '../../consts';
import { NODE_ENV } from '../../env';
import HelperTimeConvert from '../../components/Helper/TimeConvert.vue';
import UiButton from '../../components/Ui/Button.vue';
import UiBeta from '../../components/Ui/Beta.vue';

export default {
  components: {
    HelperTimeConvert,
    UiButton,
    UiBeta,
  },
  props: {
    hidePurchaseButtons: { // set to true in launchpad to hide purchase/upgrade buttons to drive Sign In's first
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    CAN_POST_MESSAGE,
    NODE_ENV,
  }),
  computed: {
    ...mapGetters(['stateData', 'showExpiretime']),
    ...mapState([
      'apiVersion',
      'forumaccount',
      'guidBlacklisted',
      'guidRegistered',
      'hideMyServers',
      'isPopUpOpen',
      'messages',
      'purchaseWindow',
      'redeemWindow',
      'registered',
      'replaceable',
      'accountRegisterPayload',
      'accountUnregisterPayload',
      'accountTrialExtendPayload',
      'accountKeyRecoverPayload',
      'accountKeyReplacePayload',
      'state',
      'iframeUrl',
    ]),
    /**
     * Just installed the plugin and now we want to override the copy for the given stateData.
     * If the user is in a non-error state drive them to sign in/up
     */
    showTrialWelcome() {
      return this.state !== 'ENOKEYFILE' && !this.state.error && !this.hideMyServers && !this.registered;
    },
    computedHeading() {
      if (this.showTrialWelcome) return this.$t('upc.thankYouForInstallingMyServers');
      return this.stateData.heading ? this.stateData.heading : this.stateData.error.heading;
    },
    computedSubheading() {
      if (this.showTrialWelcome) return this.$t('upc.connectYourUnraidnetAccountToGetStarted');
      return this.stateData.message ? this.stateData.message : this.stateData.error.message;
    },
  },
  methods: {
    ...mapActions([
      'openPopUp',
      'closePopUp',
      'closePopUpFromPopUp',
      'startTrial',
      'togglePromo',
    ]),
    ...mapMutations([
      'SET_IS_LAUNCHPAD_OPEN',
    ]),
    niceTry() {
      // eslint-disable-next-line no-alert
      alert('❌ Nice try you little sneak ❌');
    },
  },
};
</script>

<template>
  <div class="text-center w-full">
    <header>
      <h2 class="text-24px md:text-30px">
        <span v-html="computedHeading"></span>
        <ui-beta v-if="showTrialWelcome" class="title-beta relative ml-4px" />
      </h2>
      <div
        class="text-14px opacity-90 mt-12px mx-auto prose"
        :class="{
          'text-center': stateData.messageCenter || showTrialWelcome,
          'text-left': !stateData.messageCenter,
        }"
      >
        <div v-html="computedSubheading"></div>
        <HelperTimeConvert v-if="showExpiretime" class="italic text-center font-semibold mt-16px" />
      </div>
    </header>
    <div class="ActionRequired flex flex-col justify-center mt-16px">
      <template v-if="isPopUpOpen">
        <UiButton
          @btn-click="openPopUp(accountRegisterPayload)"
          :text="$t('backToPopUp')"
          class="button button-alt w-full mt-8px" />
        <UiButton
          @btn-click="closePopUp()"
          :text="$t('closePopUp')"
          class="button-fine-error text-grey-mid w-full mt-8px" />
      </template>
      <template v-else>
        <template v-if="(!hideMyServers || (apiVersion && apiVersion !== '0.0.0')) && stateData.actions.includes('register')">
          <UiButton
            @btn-click="openPopUp(accountRegisterPayload)"
            :hrefUrl="iframeUrl"
            :href-external="!!iframeUrl"
            :text="iframeUrl ? `${$t('signIn')} (new tab)` : $t('signIn')"
            class="button button-alt w-full mt-8px" />
          <UiButton
            v-if="!guidRegistered && !forumaccount"
            @btn-click="openPopUp(accountRegisterPayload)"
            :hrefUrl="iframeUrl"
            :href-external="!!iframeUrl"
            :text="iframeUrl ? `${$t('signUp')} (new tab)` : $t('signUp')"
            class="button button-alt w-full mt-8px" />
        </template>
        <!-- promo plugin if not installed and on trial. don't show in pop-up. -->
        <UiButton
          v-if="state === 'TRIAL' && hideMyServers && !CAN_POST_MESSAGE"
          @btn-click="togglePromo()"
          :text="$t('upc.enhanceYourExperienceWithMyServers')"
          class="button button-alt w-full mt-8px" />

        <!-- startTrial can only be done from Launchpad & UPC. Not the pop-up. -->
        <UiButton
          v-if="(stateData.actions.includes('startTrial') && !CAN_POST_MESSAGE)"
          @btn-click="startTrial()"
          :text="$t('actions.startTrial')"
          class="button button-alt w-full mt-8px" />

        <!-- purchase / redeem / upgrade subset of buttons -->
        <UiButton
          v-if="stateData.actions.includes('purchase')"
          @btn-click="openPopUp(purchaseWindow)"
          :hrefUrl="CAN_POST_MESSAGE ? purchaseWindow.url : iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `${$t('actions.purchase')} (new tab)` : $t('actions.purchase')"
          class="button button-alt w-full mt-8px" />
        <UiButton
          v-if="stateData.actions.includes('purchase')"
          @btn-click="openPopUp(redeemWindow)"
          :hrefUrl="CAN_POST_MESSAGE ? redeemWindow.url : iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `Redeem Activation Code (new tab)` : 'Redeem Activation Code'"
          class="button button-alt w-full mt-8px" />
        <UiButton
          v-if="stateData.actions.includes('upgrade')"
          @btn-click="openPopUp(purchaseWindow)"
          :hrefUrl="CAN_POST_MESSAGE ? purchaseWindow.url : iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `${$t('actions.upgrade')} (new tab)` : $t('actions.upgrade')"
          class="button button-alt w-full mt-8px" />
        <!-- replace / recover key -->
        <UiButton
          v-if="stateData.actions.includes('replace') && replaceable"
          @btn-click="openPopUp(accountKeyReplacePayload)"
          :hrefUrl="iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `${$t('actions.replace')} (new tab)` : $t('actions.replace')"
          class="button w-full mt-8px" />
        <UiButton
          v-if="stateData.actions.includes('replace') && !replaceable"
          :text="$t('actions.replaceIneligible')"
          :title="$t('stateData.EGUID.error.message.replacementIneligible')"
          disabled
          @btn-click="niceTry()"
          class="button w-full mt-8px" />
        <UiButton
          v-if="stateData.actions.includes('recover')"
          @btn-click="openPopUp(accountKeyRecoverPayload)"
          :hrefUrl="iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `${$t('actions.recover')} (new tab)` : $t('actions.recover')"
          class="button w-full mt-8px" />
        <!-- trial related buttons -->
        <UiButton
          v-if="stateData.actions.includes('continueTrial')"
          @btn-click="CAN_POST_MESSAGE ? closePopUpFromPopUp() : SET_IS_LAUNCHPAD_OPEN(false)"
          :text="$t('regWizPopUp.continueTrial')"
          class="button w-full mt-8px" />
        <UiButton
          v-if="stateData.actions.includes('extend')"
          @btn-click="openPopUp(accountTrialExtendPayload)"
          :hrefUrl="iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `${$t('actions.extend')} (new tab)` : $t('actions.extend')"
          class="button w-full mt-8px" />
        <!-- glorified close button -->
        <UiButton
          v-if="stateData.actions.includes('close')"
          @btn-click="CAN_POST_MESSAGE ? closePopUpFromPopUp() : SET_IS_LAUNCHPAD_OPEN(false)"
          :text="$t('continueToUnraid')"
          class="button w-full mt-8px" />
        <!-- @note - `&& CAN_POST_MESSAGE` ensures that we only display Sign Out in a pop-up -->
        <UiButton
          v-if="stateData.actions.includes('signOut') && CAN_POST_MESSAGE"
          @btn-click="openPopUp(accountUnregisterPayload)"
          :hrefUrl="iframeUrl"
          :href-external="!!iframeUrl"
          :text="iframeUrl ? `${$t('signOut')} (new tab)` : $t('signOut')"
          class="button-fine-error text-grey-mid w-full mt-8px" />
      </template>
    </div>
  </div>
</template>

<style lang="postcss">
.ActionRequired a,
.ActionRequired button {
  @apply text-14px px-24px py-16px;
}

.title-beta { top: -7px; }
</style>
